import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import api from '@/lib/api';
import {
  isBiometricAvailable,
  isBiometricEnabled,
  getCredentialsWithBiometric,
  saveCredentials,
  clearBiometricCredentials,
  getBiometryType,
} from '@/lib/biometric-auth';

/**
 * AuthContext — Gerencia autenticação para participantes do Bolão.
 *
 * Diferente do admin (que valida SUPERADMIN), aqui qualquer usuário
 * autenticado é válido. Suporta signup, confirm, login, forgot/reset
 * password, logout e refresh de sessão.
 *
 * localStorage keys:
 *   bolao_token         — JWT access token
 *   bolao_refresh_token — Refresh token
 *   bolao_user          — User profile JSON
 */

const TOKEN_KEY = 'bolao_token';
const REFRESH_TOKEN_KEY = 'bolao_refresh_token';
const USER_KEY = 'bolao_user';
const REDIRECT_KEY = 'bolao_auth_redirect';

const API_URL = import.meta.env.VITE_API_URL ?? '';

interface UserProfile {
  userId: string;
  email: string;
  nomeCompleto: string;
  fotoPerfil: string | null;
  roles: string[];
}

interface SignupInput {
  nomeCompleto: string;
  email: string;
  password: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
}

interface AuthContextType extends AuthState {
  loading: boolean;
  login(email: string, password: string): Promise<void>;
  signup(data: SignupInput): Promise<void>;
  confirmSignup(email: string, code: string): Promise<void>;
  forgotPassword(email: string): Promise<void>;
  resetPassword(email: string, code: string, newPassword: string, confirmPassword: string): Promise<void>;
  logout(): Promise<void>;
  refreshSession(): Promise<void>;
  getAuthRedirect(): string | null;
  setAuthRedirect(path: string): void;
  clearAuthRedirect(): void;
  consumeAuthRedirect(): string | null;
  // Biometric
  biometricAvailable: boolean;
  biometricEnabled: boolean;
  biometryType: 'fingerprint' | 'face' | 'none';
  loginWithBiometric(): Promise<void>;
  enableBiometric(email: string, password: string): Promise<boolean>;
  disableBiometric(): Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function loadStoredState(): AuthState {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const userJson = localStorage.getItem(USER_KEY);

  if (accessToken && userJson) {
    try {
      const user = JSON.parse(userJson) as UserProfile;
      return {
        isAuthenticated: true,
        user,
        accessToken,
        refreshToken,
      };
    } catch {
      // Corrupted data — fall through to unauthenticated
    }
  }

  return {
    isAuthenticated: false,
    user: null,
    accessToken: null,
    refreshToken: null,
  };
}

function clearStoredAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function storeAuth(accessToken: string, refreshToken: string, user: UserProfile): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(loadStoredState);
  const [loading, setLoading] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(isBiometricEnabled());
  const [biometryType, setBiometryType] = useState<'fingerprint' | 'face' | 'none'>('none');

  // On mount: restore auth state from localStorage and check biometric availability
  useEffect(() => {
    const stored = loadStoredState();
    if (stored.isAuthenticated) {
      setState(stored);
    }
    setLoading(false);

    // Check biometric availability
    isBiometricAvailable().then((available) => {
      setBiometricAvailable(available);
      if (available) {
        getBiometryType().then(setBiometryType);
      }
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    // Use raw fetch for login since api.ts expects tokens to already be set
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new Error(
        errorBody?.error?.message ?? 'E-mail ou senha incorretos',
      );
    }

    const json = await response.json();
    const data = json.data ?? json;

    const accessToken: string = data.accessToken;
    const refreshToken: string = data.refreshToken;
    const rawUser = data.user;

    // Mapear campos do backend (name, photoUrl) para frontend (nomeCompleto, fotoPerfil)
    const user: UserProfile = {
      userId: rawUser.userId,
      email: rawUser.email,
      nomeCompleto: rawUser.name ?? rawUser.nomeCompleto ?? '',
      fotoPerfil: rawUser.photoUrl ?? rawUser.fotoPerfil ?? null,
      roles: rawUser.roles ?? (rawUser.role ? [rawUser.role] : []),
    };

    if (!accessToken || !refreshToken || !user) {
      throw new Error('Resposta de login inválida');
    }

    storeAuth(accessToken, refreshToken, user);

    setState({
      isAuthenticated: true,
      user,
      accessToken,
      refreshToken,
    });
  }, []);

  const signup = useCallback(async (data: SignupInput) => {
    await api.post<{ message: string }>('/auth/signup', {
      name: data.nomeCompleto,
      email: data.email,
      password: data.password,
      confirmPassword: data.password,
    });
  }, []);

  const confirmSignup = useCallback(async (email: string, code: string) => {
    await api.post<{ message: string }>('/auth/confirm', { email, code });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await api.post<{ message: string }>('/auth/password/forgot', { email });
  }, []);

  const resetPassword = useCallback(async (email: string, code: string, newPassword: string, confirmPassword: string) => {
    await api.post<{ message: string }>('/auth/password/reset', {
      email,
      code,
      newPassword,
      confirmPassword,
    });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Best-effort — clear local state regardless
    }

    clearStoredAuth();

    setState({
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
    });
  }, []);

  const refreshSession = useCallback(async () => {
    const currentRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!currentRefreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    });

    if (!response.ok) {
      clearStoredAuth();
      setState({
        isAuthenticated: false,
        user: null,
        accessToken: null,
        refreshToken: null,
      });
      throw new Error('Sessão expirada');
    }

    const json = await response.json();
    const data = json.data ?? json;

    const newAccessToken: string = data.accessToken;
    const newRefreshToken: string | undefined = data.refreshToken;

    localStorage.setItem(TOKEN_KEY, newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    }

    setState((prev) => ({
      ...prev,
      accessToken: newAccessToken,
      refreshToken: newRefreshToken ?? prev.refreshToken,
    }));
  }, []);

  // ── Redirect management for auth flow ──

  const getAuthRedirect = useCallback((): string | null => {
    return localStorage.getItem(REDIRECT_KEY);
  }, []);

  const setAuthRedirect = useCallback((path: string): void => {
    localStorage.setItem(REDIRECT_KEY, path);
  }, []);

  const clearAuthRedirect = useCallback((): void => {
    localStorage.removeItem(REDIRECT_KEY);
  }, []);

  const consumeAuthRedirect = useCallback((): string | null => {
    const redirect = localStorage.getItem(REDIRECT_KEY);
    if (redirect) {
      localStorage.removeItem(REDIRECT_KEY);
    }
    return redirect;
  }, []);

  // ── Biometric methods ──

  const loginWithBiometric = useCallback(async () => {
    const credentials = await getCredentialsWithBiometric();
    if (!credentials) {
      // Credentials not found in Keystore (reinstall, or never properly saved)
      // Clear the stale flag so user is prompted to re-enable on next manual login
      await clearBiometricCredentials();
      setBiometricEnabled(false);
      throw new Error('Digital não cadastrada. Faça login com e-mail e senha para reativar.');
    }
    // Reuse the normal login flow with recovered credentials
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: credentials.email, password: credentials.password }),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      // If credentials are invalid (password changed), clear biometric
      if (response.status === 401) {
        await clearBiometricCredentials();
        setBiometricEnabled(false);
      }
      throw new Error(errorBody?.error?.message ?? 'Falha no login biométrico');
    }

    const json = await response.json();
    const data = json.data ?? json;

    const accessToken: string = data.accessToken;
    const refreshToken: string = data.refreshToken;
    const rawUser = data.user;

    const user: UserProfile = {
      userId: rawUser.userId,
      email: rawUser.email,
      nomeCompleto: rawUser.name ?? rawUser.nomeCompleto ?? '',
      fotoPerfil: rawUser.photoUrl ?? rawUser.fotoPerfil ?? null,
      roles: rawUser.roles ?? (rawUser.role ? [rawUser.role] : []),
    };

    storeAuth(accessToken, refreshToken, user);
    setState({ isAuthenticated: true, user, accessToken, refreshToken });
  }, []);

  const enableBiometric = useCallback(async (email: string, password: string): Promise<boolean> => {
    const saved = await saveCredentials({ email, password });
    if (saved) {
      setBiometricEnabled(true);
    }
    return saved;
  }, []);

  const handleDisableBiometric = useCallback(async () => {
    await clearBiometricCredentials();
    setBiometricEnabled(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loading,
        login,
        signup,
        confirmSignup,
        forgotPassword,
        resetPassword,
        logout,
        refreshSession,
        getAuthRedirect,
        setAuthRedirect,
        clearAuthRedirect,
        consumeAuthRedirect,
        biometricAvailable,
        biometricEnabled,
        biometryType,
        loginWithBiometric,
        enableBiometric,
        disableBiometric: handleDisableBiometric,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
}
